import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { BeoordelingModal, type BeoordeelActie } from '../components/BeoordelingModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

const FASE: Record<string, { label: string; cls: string }> = {
  aanvraag: { label: 'Te beoordelen — aanvraag', cls: 'bg-orange-100 text-orange-700' },
  te_beoordelen: { label: 'Te beoordelen', cls: 'bg-amber-100 text-amber-700' },
  lopend: { label: 'Lopend', cls: 'bg-emerald-100 text-emerald-700' },
  afgemeld_betaald: { label: 'Afgemeld — betaald', cls: 'bg-slate-100 text-slate-600' },
  afgemeld_dossier: { label: 'Afgemeld — incasso/schade', cls: 'bg-slate-100 text-slate-600' },
};

export const AdminVerzekeringenPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fase, setFase] = useState<string>('alle');
  const [toonArchief, setToonArchief] = useState(false);
  const notify = useToast();
  const [beoordeel, setBeoordeel] = useState<any>(null);   // rij die beoordeeld wordt
  const [afmeld, setAfmeld] = useState<any>(null);         // rij die afgemeld wordt (buiten termijn)

  const verzekerActies = (r: any): BeoordeelActie[] => [
    {
      key: 'goedkeuren', label: 'Goedkeuren', kleur: 'green',
      template_key: 'verzekering_goedgekeurd', bijlage: 'Verzekeringsbewijs (PDF)',
      context: (_red, toel) => ({ factuur: r.factuur, dekking: fmt(r.dekking), toelichting: toel }),
    },
    {
      key: 'afkeuren', label: 'Afwijzen', kleur: 'red',
      redenen: ['Niet verzekerbaar', 'Buiten dekking', 'Te hoog risico', 'Onvoldoende gegevens', 'Anders'],
      template_key: 'verzekering_afgewezen', bijlage: 'Afwijzingsbrief (PDF)',
      context: (red, toel) => ({ factuur: r.factuur, reden: red, toelichting: toel }),
    },
  ];

  const afmeldActies = (r: any): BeoordeelActie[] => [
    {
      key: 'afmelden', label: 'Dekking afmelden', kleur: 'red',
      redenen: [
        'Buiten termijn — niet tijdig ter incasso overgedragen',
        'Betalingsconditie + achterstalligheid + incassotermijn overschreden',
        'Op verzoek van het lid',
        'Anders',
      ],
      template_key: 'verzekering_afgemeld', bijlage: 'Afmeldingsbrief (PDF)',
      context: (red, toel) => ({ factuur: r.factuur, reden: red, toelichting: toel, uiterlijk: r.uiterlijk_dagen }),
    },
  ];

  const voerAfmeldUit = async (invoiceId: string, _actieKey: string, reden: string, toelichting: string, onderwerp: string, bericht: string) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/verzekeringen/${invoiceId}/afmelden`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reden, toelichting, onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); notify.error(e.detail || 'Afmelden mislukt'); return null; }
    notify.success('Dekking afgemeld — bericht en e-mail verzonden naar het lid.');
    return res.json();
  };

  const voerVerzekerUit = async (invoiceId: string, actieKey: string, reden: string, toelichting: string, onderwerp: string, bericht: string) => {
    const path = actieKey === 'goedkeuren' ? 'goedkeuren' : 'afkeuren';
    const basis: any = actieKey === 'goedkeuren' ? { toelichting } : { reason: reden, toelichting };
    const res = await fetch(`${API_BASE_URL}/api/admin/verzekeringen/${invoiceId}/${path}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...basis, onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); notify.error(e.detail || 'Actie mislukt'); return null; }
    notify.success(actieKey === 'goedkeuren' ? 'Verzekering goedgekeurd — bericht verzonden.' : 'Verzekering afgewezen — bericht verzonden.');
    return res.json();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/verzekeringen`, { headers, cache: 'no-store' });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const archiveer = async (id: string, gearchiveerd: boolean) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/archiveren/verzekering/${id}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ gearchiveerd }),
    });
    if (!res.ok) { notify.error('Archiveren mislukt'); return; }
    notify.success(gearchiveerd ? 'Verzekering naar archief verplaatst.' : 'Teruggehaald uit archief.');
    load();
  };

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const ARCHIEF = ['afgemeld_betaald', 'afgemeld_dossier'];
  const count = (f: string) => rows.filter((r) => r.fase === f).length;
  const buitenTermijnCount = rows.filter((r) => r.buiten_termijn).length;
  const zichtbaar = (r: any) => toonArchief || !r.gearchiveerd;
  const shown = (fase === 'alle'
    ? (toonArchief ? rows : rows.filter((r) => !ARCHIEF.includes(r.fase) && !r.gearchiveerd))
    : fase === 'buiten_termijn'
      ? rows.filter((r) => r.buiten_termijn && zichtbaar(r))
      : rows.filter((r) => r.fase === fase && zichtbaar(r)));
  const totaalDekking = shown.reduce((a, r) => a + (r.dekking || 0), 0);

  return (
    <Layout>
      <p className="text-sm text-slate-500 mb-6">
        Levenscyclus van verzekerde facturen — van te beoordelen tot afgemeld na betaling, incasso of schade.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <Card label="Aanvragen te beoordelen" value={count('aanvraag')} tone="orange" onClick={() => setFase('aanvraag')} active={fase === 'aanvraag'} />
        <Card label="Schade te beoordelen" value={count('te_beoordelen')} tone="amber" onClick={() => setFase('te_beoordelen')} active={fase === 'te_beoordelen'} />
        <Card label="Lopend" value={count('lopend')} tone="emerald" onClick={() => setFase('lopend')} active={fase === 'lopend'} />
        <Card label="Buiten termijn — afmelden" value={buitenTermijnCount} tone="red" onClick={() => setFase('buiten_termijn')} active={fase === 'buiten_termijn'} />
        <Card label="Afgemeld — betaald" value={count('afgemeld_betaald')} tone="slate" onClick={() => setFase('afgemeld_betaald')} active={fase === 'afgemeld_betaald'} />
        <Card label="Afgemeld — incasso/schade" value={count('afgemeld_dossier')} tone="slate" onClick={() => setFase('afgemeld_dossier')} active={fase === 'afgemeld_dossier'} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setFase('alle')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${fase === 'alle' ? 'bg-mfb-blue text-white border-mfb-blue' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>Alle</button>
        <label className="ml-2 inline-flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={toonArchief} onChange={(e) => setToonArchief(e.target.checked)} /> Toon archief (afgemeld + gearchiveerd)
        </label>
        <span className="ml-auto text-sm text-slate-500">{shown.length} verzekering(en) · dekking {fmt(totaalDekking)}</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen verzekeringen in deze selectie.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Factuur', 'Lid / debiteur', 'Grade', 'Factuurbedrag', 'Dekking', 'Vervaldatum', 'Fase', 'Beheren'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => {
                const f = FASE[r.fase] || { label: r.fase_label, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={r.invoice_id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-mfb-blue">{r.factuur}
                      {r.buiten_bundel && <span className="ml-2 text-[10px] text-mfb-orange">buiten bundel</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-700">{r.lid}<div className="text-xs text-slate-400">{r.debiteur} · {r.collectiviteit}</div></td>
                    <td className="px-5 py-3 text-slate-700">{r.grade ?? '—'}</td>
                    <td className="px-5 py-3 text-slate-700">{fmt(r.factuurbedrag)}</td>
                    <td className="px-5 py-3 text-slate-700">{fmt(r.dekking)}</td>
                    <td className="px-5 py-3 text-slate-600">{r.vervaldatum}
                      {r.buiten_termijn && <div className="text-[10px] text-red-600 mt-0.5">buiten termijn t/m {r.termijn_grens}</div>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${f.cls}`}>{f.label}</span>
                      {r.buiten_termijn && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">buiten termijn</span>}
                      {r.gearchiveerd && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-600">gearchiveerd</span>}
                      {r.dossier && <div className="text-xs text-slate-400 mt-0.5">{r.dossier}</div>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {r.fase === 'aanvraag' ? (
                          <button onClick={() => setBeoordeel(r)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-mfb-blue hover:bg-mfb-blue-dark text-white">Beoordelen</button>
                        ) : r.fase === 'lopend' ? (
                          <button onClick={() => setAfmeld(r)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${r.buiten_termijn ? 'bg-red-600 hover:bg-red-700 text-white' : 'border border-red-300 text-red-700 hover:bg-red-50'}`}>Afmelden</button>
                        ) : null}
                        {r.gearchiveerd
                          ? <button onClick={() => archiveer(r.invoice_id, false)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50">Terughalen</button>
                          : <button onClick={() => archiveer(r.invoice_id, true)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-50" title="Naar archief">Archiveer</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}

      {beoordeel && (
        <BeoordelingModal
          titel="Verzekeringsaanvraag beoordelen"
          context={`Factuur ${beoordeel.factuur} · debiteur ${beoordeel.debiteur} · dekking ${fmt(beoordeel.dekking)}`}
          acties={verzekerActies(beoordeel)} token={accessToken!} invoiceId={beoordeel.invoice_id}
          uitvoeren={(actieKey, reden, toel, ond, ber) => voerVerzekerUit(beoordeel.invoice_id, actieKey, reden, toel, ond, ber)}
          onClose={() => setBeoordeel(null)} onDone={() => load()}
        />
      )}

      {afmeld && (
        <BeoordelingModal
          titel="Verzekeringsdekking afmelden"
          context={`Factuur ${afmeld.factuur} · debiteur ${afmeld.debiteur} · dekking ${fmt(afmeld.dekking)}${afmeld.buiten_termijn ? ` · buiten termijn t/m ${afmeld.termijn_grens}` : ''}`}
          acties={afmeldActies(afmeld)} token={accessToken!} invoiceId={afmeld.invoice_id}
          uitvoeren={(actieKey, reden, toel, ond, ber) => voerAfmeldUit(afmeld.invoice_id, actieKey, reden, toel, ond, ber)}
          onClose={() => setAfmeld(null)} onDone={() => load()}
        />
      )}
    </Layout>
  );
};

function Card({ label, value, tone, onClick, active }: { label: string; value: number; tone: 'amber' | 'emerald' | 'slate' | 'orange' | 'red'; onClick: () => void; active: boolean }) {
  const map: Record<string, string> = {
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    slate: 'text-slate-600 bg-slate-50 border-slate-200',
    orange: 'text-orange-700 bg-orange-50 border-orange-200',
    red: 'text-red-700 bg-red-50 border-red-200',
  };
  return (
    <button onClick={onClick} className={`text-left rounded-xl border p-4 transition ${map[tone]} ${active ? 'ring-2 ring-mfb-blue' : ''}`}>
      <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
      <div className="text-xs font-medium mt-0.5">{label}</div>
    </button>
  );
}

export default AdminVerzekeringenPage;
